import { Suspense, useEffect, lazy } from 'react';
import cnLocale from 'antd/es/locale/zh_CN';
import enLocale from 'antd/es/locale/en_US';
import idLocale from 'antd/es/locale/id_ID';
import thLocale from 'antd/es/locale/th_TH';
// import esLocale from 'antd/es/locale/es_ES';
import viLocale from 'antd/es/locale/vi_VN';
import ptLocale from 'antd/es/locale/pt_BR';
import { ThemeProvider } from '@emotion/react';
import { ConfigProvider, message, notification } from 'antd';

import ErrorBoundary, { ErrorResult } from 'components/ErrorBoundary';
import AppLoading from 'components/AppLoading';
import Router from 'components/Router';
import Authorization, { onAuthorize, unauthorize } from 'components/Authorization';
import authorization from 'services/config/authorization';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import mjtTheme from 'themes/mlj';
import chatttSDK, { getVersion, isMlj } from 'services/chattt';
import 'services/nim/nimHealth';
import { ThemesProvider as FettThemesProvider } from 'components/ThemesProvider';
import useAuthShops from 'hooks/business/authShops/useShopeeAuthShops';
import { useSetIsChatttUpdate, useSetIsFettUpdate } from 'hooks/useUpdater';
import { isDemoChannel, isDemoOrgs } from 'services/channels/demo';
import NetScanModal from 'views/Setting/components/NetScanModal';
import { getStopMessageHandlerState } from 'hooks/business/authShops/useStopMessageHandler';
import { getAppVersionInfo } from 'services/api/notice';
import { useMemoizedFn } from 'ahooks';
import { isBelowWindows10 } from 'utils/is';
import { compare } from 'compare-versions';
import { TolgeeProvider } from '@tolgee/react';
import { tolgee } from 'services/i18n/tolgee';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import CloseClient from 'views/Main/components/CloseClient';
import { Provider as JotaiRootProvider } from 'jotai';

const Notification = lazy(() => import('components/Notification'));
const UpdaterModal = lazy(() => import('components/UpdaterModal'));

const {
  ConfigContext: { Consumer: ConfigConsumer },
} = ConfigProvider;

notification.config({
  maxCount: 3,
});

if (thLocale.Pagination) {
  thLocale.Pagination.jump_to = 'ไปยังหน้า';
  thLocale.Pagination.page = '';
}

const languageLocaleMap = {
  'zh-CN': cnLocale,
  'en-US': enLocale,
  'zh-TW': cnLocale,
  id: idLocale,
  th: thLocale,
  vi: viLocale,
  pt: ptLocale,
  tl: enLocale,
  ms: enLocale,
  es: enLocale,
  pl: enLocale,
  fr: enLocale,
};

ConfigProvider.config({
  theme: isMlj ? mjtTheme : undefined,
});

// 全局 message 配置
message.config({
  maxCount: 5,
});

function Core() {
  return (
    <>
      <CloseClient />
      {/* NetScanModal scan行为过于繁琐，直接挂载一个节点进行监听message以执行非渲染时的 */}
      <NetScanModal trigger="message" />
      <Notification />
      <UpdaterModal />
      <Router />
    </>
  );
}

function Entry() {
  const language = useSystemLanguageValue();
  const locale = languageLocaleMap[language];
  const { messageHandler } = useAuthShops();
  const setIsChatttUpdate = useSetIsChatttUpdate();
  const setIsFettUpdate = useSetIsFettUpdate();

  const updateChattt = useMemoizedFn(async (payload: any) => {
    const setState = (releaseNotes: any) => {
      setIsChatttUpdate({
        value: true,
        updateInfo: {
          version: payload?.version,
          releaseDate: payload?.releaseDate,
          releaseNotes,
        },
      });
    };

    const clientInfo = await chatttSDK?.app?.info?.();
    if (clientInfo?.appId && payload?.version) {
      getAppVersionInfo({
        appId: clientInfo.appId,
        version: payload.version,
      })
        .then((response) => {
          setState(response?.versionDescMap);
        })
        .catch(() => {
          setState('');
        });
    } else {
      setState('');
    }
  });

  useEffect(() => {
    const handler = (evt: MessageEvent) => {
      const { event: eventName, payload } = evt.data;
      if (!getStopMessageHandlerState()) {
        messageHandler(evt);
      }

      if (eventName === 'app:updateDownloaded' && !isBelowWindows10()) {
        // chattt 2.16.0 版本升级 Electron 后
        // 将区分 Win10 和非 Win10 的更新
        // 在此对于 2.16.0 以下版本，不再触发更新
        // 对于 2.16.0 后的 Win10 系统以下版本，统一走特殊的 Electron 版本分支发布
        if (isBelowWindows10() && compare(getVersion(), '2.16.0', '<')) {
          return;
        }

        updateChattt(payload);
      }
      if (eventName === 'fettApp:readyToUpdate') {
        setIsFettUpdate({
          value: true,
          updateInfo: {
            name: payload?.name,
            version: payload?.version,
          },
        });
      }
    };

    window.addEventListener('message', handler, false);
    return () => {
      window.removeEventListener('message', handler, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConfigProvider locale={locale} autoInsertSpaceInButton={false}>
      <ConfigConsumer>
        {({ getPrefixCls }) => {
          return (
            <ThemeProvider theme={{ getAntdPrefixedClassName: getPrefixCls }}>
              <FettThemesProvider>
                <Authorization
                  storage={authorization.storage}
                  cacheKey={authorization.cacheKey}
                  queryKey={authorization.queryKey}
                  onAuthorize={authorization.onAuthorize}
                  onUnauthorize={authorization.onUnauthorize}
                >
                  <Core />
                </Authorization>
              </FettThemesProvider>
            </ThemeProvider>
          );
        }}
      </ConfigConsumer>
    </ConfigProvider>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

/**
 * 应用入口
 */
function App() {
  useEffect(() => {
    chatttSDK?.app?.loadNormal?.();
  }, []);

  useEffect(() => {
    if (window.location.hash === '#_=_') {
      window.history.replaceState(null, '', window.location.href.split('#')[0]);
    }
  }, []);

  return (
    <ErrorBoundary fallback={() => <ErrorResult />}>
      <Suspense fallback={<AppLoading />}>
        <QueryClientProvider client={queryClient}>
          <JotaiRootProvider>
            <TolgeeProvider tolgee={tolgee} fallback={<AppLoading />}>
              <Entry />
            </TolgeeProvider>
          </JotaiRootProvider>
        </QueryClientProvider>
      </Suspense>
    </ErrorBoundary>
  );
}

onAuthorize<Oversea.Token.Payload>((_t, paylod) => {
  if (!isDemoChannel) {
    if (isDemoOrgs(paylod.orgId)) {
      message.error('演示帐号请使用专用包登录');
      unauthorize('@EVENT_TOKEN_FORBIDDEN', '演示帐号请使用专用包登录');
    }
  }
});

export default App;
